// ---------------------------------------------------------------------------
// Weekly narrative derivation — fills the three lines that lead the weekly
// PDF export: "What happened / What it means / What's next". Pure functions
// mirror the backend defaults in PdfExportService, so the dialog the user
// sees pre-filled matches what they'd get from the bare GET export.
// ---------------------------------------------------------------------------

import type { WorkItem } from '../types/models';

export interface WeeklyNarrativeInput {
  totalItems: number;
  completedItems: number;
  carriedForward: number;
  completionRate: number;
  totalPlannedMinutes: number;
  totalMeetingMinutes: number;
  keyRiskCount: number;
}

export interface WeeklyNarrative {
  whatHappened: string;
  whatItMeans: string;
  whatsNext: string;
}

function toHours(minutes: number): number {
  return Math.round((minutes / 60) * 10) / 10;
}

export function deriveWeeklyNarrative(input: WeeklyNarrativeInput): WeeklyNarrative {
  const {
    totalItems,
    completedItems,
    carriedForward,
    completionRate,
    totalPlannedMinutes,
    totalMeetingMinutes,
    keyRiskCount,
  } = input;

  // What happened
  let whatHappened: string;
  if (totalItems === 0) {
    whatHappened = 'No work items were tracked against this week.';
  } else {
    whatHappened =
      `${completedItems} of ${totalItems} planned items closed ` +
      `(${completionRate.toFixed(1)}% completion). ` +
      `${toHours(totalPlannedMinutes)}h planned work, ${toHours(totalMeetingMinutes)}h meetings.`;
  }

  // What it means
  let whatItMeans: string;
  if (totalItems === 0) {
    whatItMeans = 'Nothing was logged, so there is no signal to draw yet.';
  } else if (completionRate >= 90) {
    whatItMeans = 'The week ran to plan, capacity matched the commitments.';
  } else if (completionRate >= 70) {
    whatItMeans = 'The week mostly ran to plan, with a modest carry-forward tail.';
  } else if (carriedForward > completedItems) {
    whatItMeans = 'More items carried forward than closed, capacity was short.';
  } else {
    whatItMeans = 'A meaningful share of planned work slipped this week.';
  }

  // What's next
  let whatsNext: string;
  if (carriedForward > 0) {
    whatsNext =
      `Clear the ${carriedForward} item${carriedForward === 1 ? '' : 's'} carried forward before adding new commitments next week.`;
  } else if (keyRiskCount > 0) {
    whatsNext = 'Address the open risks flagged below before they compound.';
  } else {
    whatsNext = 'Re-confirm priorities against the strategic drivers and plan the coming week.';
  }

  return { whatHappened, whatItMeans, whatsNext };
}

// Convenience: derive from a filtered-by-week WorkItem[] the same way the
// Reports page already computes the totals for the My Reports tab.
export function weeklyNarrativeFromWorkItems(
  items: WorkItem[],
  options?: { totalPlannedMinutes?: number; totalMeetingMinutes?: number; keyRiskCount?: number },
): WeeklyNarrative {
  const totalItems = items.length;
  const completedItems = items.filter((i) => i.status === 'Done').length;
  const carriedForward = items.filter((i) => i.isCarryForward).length;
  const completionRate = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return deriveWeeklyNarrative({
    totalItems,
    completedItems,
    carriedForward,
    completionRate,
    totalPlannedMinutes: options?.totalPlannedMinutes ?? 0,
    totalMeetingMinutes: options?.totalMeetingMinutes ?? 0,
    keyRiskCount: options?.keyRiskCount ?? 0,
  });
}
